import React, { useEffect, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RefreshCw, ChevronDown, Check } from "lucide-react";

const INTERVAL_OPTIONS = [
  { label: "Off",       value: 0 },
  { label: "10 sec",    value: 10_000 },
  { label: "30 sec",    value: 30_000 },
  { label: "1 min",     value: 60_000 },
  { label: "5 min",     value: 300_000 },
];

function timeAgo(ts) {
  if (!ts) return "just now";
  const d = Math.floor((Date.now() - ts) / 1000);
  if (d < 5) return "just now";
  if (d < 60) return `${d}s ago`;
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  return `${Math.floor(d / 3600)}h ago`;
}

export default function RefreshControl({ onRefresh, storageKey = "autoRefreshMs", testId = "refresh" }) {
  const [interval, setInterval] = useState(() => {
    const v = parseInt(localStorage.getItem(storageKey) || "0", 10);
    return Number.isFinite(v) ? v : 0;
  });
  const [busy, setBusy] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(Date.now());
  const [, tick] = useState(0);
  const timerRef = useRef(null);
  // Always call the LATEST onRefresh, even if the parent hands us a new one
  // (e.g., because URL params changed and the parent rebuilt its reload callback).
  // Without this, the interval timer would keep invoking a stale reload bound to
  // the previous scope, silently overwriting the current scope's data.
  const onRefreshRef = useRef(onRefresh);
  useEffect(() => { onRefreshRef.current = onRefresh; }, [onRefresh]);
  const busyRef = useRef(false);

  // Tick every 5s to update "Xs ago" display
  useEffect(() => {
    const id = window.setInterval(() => tick((n) => n + 1), 5000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => { localStorage.setItem(storageKey, String(interval)); }, [interval, storageKey]);

  const doRefresh = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try { await onRefreshRef.current?.(); }
    finally {
      busyRef.current = false;
      setBusy(false);
      setLastRefreshed(Date.now());
    }
  };

  // Auto-refresh timer — uses `onRefreshRef` so it always calls the current
  // reload callback (survives parent prop changes without needing to re-arm).
  // DO NOT add `doRefresh` or `onRefresh` to this dep array — the refs are the
  // source of freshness. Rebinding the interval on every render would drift the
  // fire schedule and reintroduce the stale-closure bug we fixed.
  useEffect(() => {
    if (timerRef.current) window.clearInterval(timerRef.current);
    if (interval > 0) {
      timerRef.current = window.setInterval(() => { doRefresh(); }, interval);
    }
    return () => { if (timerRef.current) window.clearInterval(timerRef.current); };
    // eslint-disable-next-line
  }, [interval]);

  const currentOption = INTERVAL_OPTIONS.find((o) => o.value === interval) || INTERVAL_OPTIONS[0];

  return (
    <div className="inline-flex items-stretch rounded-md border border-slate-200 bg-white overflow-hidden">
      <button
        onClick={doRefresh}
        disabled={busy}
        className="px-2.5 flex items-center gap-1.5 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition"
        data-testid={`${testId}-btn`}
        title={`Last refreshed ${timeAgo(lastRefreshed)}`}
      >
        <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin text-purple-500" : ""}`} />
        <span className="hidden sm:inline">{busy ? "Refreshing…" : `Updated ${timeAgo(lastRefreshed)}`}</span>
      </button>
      <Popover>
        <PopoverTrigger asChild>
          <button
            className="px-2 border-l border-slate-200 text-slate-600 hover:bg-slate-50 flex items-center gap-1 text-[11px]"
            data-testid={`${testId}-interval-btn`}
            title="Auto-refresh interval"
          >
            <span className="font-medium">{interval === 0 ? "Auto" : currentOption.label}</span>
            <ChevronDown className="h-3 w-3" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-48 p-1" data-testid={`${testId}-menu`}>
          <div className="text-[10px] font-semibold text-slate-500 uppercase px-2 pt-1 pb-1.5">Auto-refresh every</div>
          {INTERVAL_OPTIONS.map((o) => (
            <button
              key={o.value}
              onClick={() => setInterval(o.value)}
              data-testid={`${testId}-opt-${o.value}`}
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm hover:bg-slate-100 ${o.value === interval ? "text-purple-700 font-medium" : "text-slate-700"}`}
            >
              {o.label}
              {o.value === interval && <Check className="h-3.5 w-3.5 ml-auto" />}
            </button>
          ))}
        </PopoverContent>
      </Popover>
    </div>
  );
}
